import type { Vec3Like } from './Vec3';

type Axis = 'x' | 'y' | 'z';

interface IndexedPoint {
  index: number;
  point: Vec3Like;
}

interface KdNode extends IndexedPoint {
  axis: Axis;
  left: KdNode | null;
  right: KdNode | null;
}

const AXES: readonly Axis[] = ['x', 'y', 'z'];

function buildKdTree(points: IndexedPoint[], depth = 0): KdNode | null {
  if (points.length === 0) return null;
  const axis = AXES[depth % AXES.length]!;
  points.sort((first, second) => first.point[axis] - second.point[axis]);
  const median = Math.floor(points.length / 2);
  const selected = points[median]!;
  return {
    ...selected,
    axis,
    left: buildKdTree(points.slice(0, median), depth + 1),
    right: buildKdTree(points.slice(median + 1), depth + 1),
  };
}

function squaredDistance(first: Vec3Like, second: Vec3Like): number {
  const dx = first.x - second.x;
  const dy = first.y - second.y;
  const dz = first.z - second.z;
  return dx * dx + dy * dy + dz * dz;
}

function nearestDistanceSquared(
  node: KdNode | null,
  query: IndexedPoint,
  currentBest: number,
): number {
  if (!node) return currentBest;

  let best = currentBest;
  if (node.index !== query.index) best = Math.min(best, squaredDistance(query.point, node.point));

  const delta = query.point[node.axis] - node.point[node.axis];
  const near = delta <= 0 ? node.left : node.right;
  const far = delta <= 0 ? node.right : node.left;
  best = nearestDistanceSquared(near, query, best);
  if (delta * delta < best) best = nearestDistanceSquared(far, query, best);
  return best;
}

/** Exact mean distance from every point to its nearest distinct neighbour. */
export function meanNearestNeighbourDistance(points: readonly Vec3Like[]): number | null {
  if (points.length < 2) return null;
  const indexed = points.map((point, index) => ({ point, index }));
  const tree = buildKdTree([...indexed]);
  let total = 0;
  for (const point of indexed) {
    total += Math.sqrt(nearestDistanceSquared(tree, point, Number.POSITIVE_INFINITY));
  }
  return total / indexed.length;
}
